import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Navbar } from '../../components/navbar'
import { Footer } from '../../components/footer'
import { DealSubmissionFlow } from '../../submission/components/deal-submission/deal-submission-flow'
import { V1SimplifiedEdit } from '../../submission/components/deal-submission/v1-simplified-edit'
import {
  fetchDealFormData,
  fetchDealById,
  updateDeal,
  deepMergeDealPayload,
} from '../../submission/lib/deal-api'
import type { DealFormData } from '../../submission/lib/types'
import { ERC4626_SALE_TYPE } from '../../submission/lib/constants'

const SubmissionEditPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>()
  const [formData, setFormData] = useState<DealFormData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [isV1Contract, setIsV1Contract] = useState(false)

  useEffect(() => {
    let isMounted = true
    if (!projectId) {
      setLoadError('Missing deal identifier.')
      setIsLoading(false)
      setFormData(null)
      return
    }
    setIsLoading(true)
    setLoadError(null)

    fetchDealById(projectId)
      .then((rawDeal) => {
        if (!isMounted) return

        const v1Metadata = rawDeal?.metadata?.v1
        const hasV1Metadata = v1Metadata !== undefined
        const isV1 = hasV1Metadata && v1Metadata?.saleType !== ERC4626_SALE_TYPE

        setIsV1Contract(isV1)

        return fetchDealFormData(projectId)
      })
      .then((data) => {
        if (!isMounted) return
        setFormData(data)
      })
      .catch((error) => {
        console.error('Failed to load deal details', error)
        if (!isMounted) return
        setLoadError('Unable to load deal details. Please try again later.')
        setFormData(null)
      })
      .finally(() => {
        if (isMounted) setIsLoading(false)
      })
    return () => {
      isMounted = false
    }
  }, [projectId])

  const handleUpdate = useCallback(
    async (payload: any) => {
      if (!projectId) throw new Error('Deal identifier is missing.')
      const existingDeal = await fetchDealById(projectId)
      const merged = deepMergeDealPayload(existingDeal, payload)
      // The vault form owns the complete v1 contract. Replacing this section
      // allows optional fields to be cleared and prevents stale, non-contract
      // values (for example copied TVL or capacity) from surviving an edit.
      if (payload?.metadata?.v1?.saleType === ERC4626_SALE_TYPE) {
        merged.metadata = {
          ...merged.metadata,
          v1: payload.metadata.v1,
        }
      }
      // A reward model's fields depend on its kind, so replace it whole:
      // merging would leave the previous kind's numbers behind.
      const rewardModel = payload?.metadata?.v1?.tvl?.rewardModel
      if (rewardModel) {
        merged.metadata = {
          ...merged.metadata,
          v1: {
            ...merged.metadata.v1,
            tvl: { ...merged.metadata.v1.tvl, rewardModel },
          },
        }
      }
      if (typeof existingDeal?.approved === 'boolean') {
        merged.approved = existingDeal.approved
      }
      if (typeof existingDeal?.is_visible === 'boolean') {
        merged.is_visible = existingDeal.is_visible
      }
      await updateDeal(projectId, merged)
    },
    [projectId],
  )

  return (
    <div className="flex flex-col min-h-screen bg-neutral-white">
      <Navbar />
      <main className="flex-1 flex flex-col">
        {isV1Contract && formData && projectId ? (
          <V1SimplifiedEdit
            formData={formData}
            dealId={projectId}
            onSubmit={handleUpdate}
            isLoading={isLoading}
          />
        ) : (
          <DealSubmissionFlow
            mode="edit"
            initialData={formData}
            isLoading={isLoading}
            onSubmit={handleUpdate}
            successNextSteps={[
              'Our team will review the updated information.',
              'You will be notified once the status is updated or if additional details are required.',
            ]}
          />
        )}
        {loadError && !isLoading && (
          <div className="max-w-3xl mx-auto w-full px-4 pb-12">
            <div className="rounded-md border border-status-error/40 bg-red-50 p-4 text-sm text-status-error">
              {loadError}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  )
}

export default SubmissionEditPage
